import { Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { THEMES, HEADING_FONTS } from "@/lib/siteThemes";

export const Box = ({ title, desc, children, testId }) => (
  <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-4" data-testid={testId}>
    <div><p className="font-heading font-bold">{title}</p>{desc && <p className="text-xs text-slate-500 mt-1">{desc}</p>}</div>
    {children}
  </section>
);
export const L = ({ label, hint, children }) => (
  <label className="block space-y-1">
    <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-500">{hint}</span>}
  </label>
);

export default function DesignTab({ site, set }) {
  return (
    <div className="space-y-6">
      <Box title="Theme" desc="Pick a look for your showroom. You can switch any time." testId="website-theme-box">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(THEMES).map(([k, t]) => {
            const on = site.theme === k;
            return (
              <button key={k} onClick={() => set({ theme: k })} data-testid={`website-theme-${k}`}
                className={`relative text-left rounded-xl overflow-hidden border-2 transition-colors ${on ? "border-amber-400" : "border-slate-800 hover:border-slate-600"}`}>
                <div className="h-28 relative" style={{ background: t.bg }}>
                  <img src={t.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 p-3 flex flex-col justify-end" style={{ background: `linear-gradient(to top, ${t.bg}, transparent)` }}>
                    <p style={{ fontFamily: `'${t.hfont}', serif`, color: t.text }} className="text-lg leading-none">Aa Jewels</p>
                    <span className="mt-2 h-1.5 w-10 rounded-full" style={{ background: site.theme === k && site.accent ? site.accent : t.accent }} />
                  </div>
                </div>
                <p className="px-3 py-2 text-xs font-semibold bg-slate-900">{t.name}</p>
                {on && <Check size={14} className="absolute top-2 right-2 text-amber-300" />}
              </button>
            );
          })}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Own accent colour" hint="Leave as theme default or pick your brand colour">
            <div className="flex gap-2">
              <input type="color" value={site.accent || THEMES[site.theme].accent} onChange={(e) => set({ accent: e.target.value })} className="h-9 w-14 rounded border border-slate-700 bg-transparent" data-testid="website-accent-input" />
              <button className="cf-btn-ghost h-9 text-xs" onClick={() => set({ accent: "" })} data-testid="website-accent-reset">Use theme colour</button>
            </div>
          </L>
          <L label="Heading font">
            <select className="cf-input h-9 text-sm" value={site.heading_font} onChange={(e) => set({ heading_font: e.target.value })} data-testid="website-font-select">
              <option value="">Theme default ({THEMES[site.theme].hfont})</option>
              {HEADING_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
            </select>
          </L>
        </div>
      </Box>

      <Box title="Website link & domain" testId="website-link-box">
        <label className="flex items-center justify-between text-sm">
          Website is live
          <Switch checked={site.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="website-enabled-switch" />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Your link" hint={`${window.location.origin}/store/${site.slug || "…"}`}>
            <input className="cf-input h-9 text-sm font-mono" value={site.slug} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="website-slug-input" />
          </L>
          <L label="Own domain (optional)" hint="e.g. newaminajewellers.com — point its DNS to this app after going live. Leave empty to use the link above.">
            <input className="cf-input h-9 text-sm font-mono" placeholder="yourstore.com" value={site.domain} onChange={(e) => set({ domain: e.target.value })} data-testid="website-domain-input" />
          </L>
        </div>
      </Box>

      <Box title="When a customer scans the card QR" desc="Applies to cards printed after you save." testId="website-qr-box">
        <div className="grid sm:grid-cols-2 gap-3">
          {[["website", "Open inside my website", "Certificate shows in your theme, with your collection and contact."], ["plain", "Open the plain certificate page", "Simple verification page with your logo and contacts."]].map(([k, label, d]) => (
            <button key={k} onClick={() => set({ qr_target: k })} data-testid={`website-qr-${k}`}
              className={`text-left rounded-lg border p-4 transition-colors ${site.qr_target === k ? "border-amber-400 bg-amber-500/10" : "border-slate-800 hover:border-slate-600"}`}>
              <p className={`text-sm font-semibold ${site.qr_target === k ? "text-amber-300" : ""}`}>{label}</p>
              <p className="text-xs text-slate-500 mt-1">{d}</p>
            </button>
          ))}
        </div>
      </Box>
    </div>
  );
}
